
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Select, TextField } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const LABEL_COLOR = 'var(--vo-ink-2)';
const BORDER_COLOR = 'var(--vo-steel)';
const CARD_SHADOW = '0 1px 3px var(--vo-escuro-8)';

const FormCard = styled.div`
  max-width: 720px;
  background: white;
  border-radius: 14px;
  padding: 32px;
  box-shadow: ${CARD_SHADOW};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--vo-cloud);
`;

const CardIconBadge = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;

const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;

const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${LABEL_COLOR};
`;

const FieldHelper = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;

const FieldError = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: var(--vo-danger);
`;

const ColorsSectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 14px;
`;

const ColorsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
`;

const ColorPickerRow = styled.div`
  height: 48px;
  border: 1.5px solid ${BORDER_COLOR};
  border-radius: 10px;
  padding: 0 12px;
  background: white;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  &:focus-within { border-color: var(--vo-celeste); }
`;

const ColorSwatch = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: ${({ $color }) => $color || 'var(--vo-ink)'};
  border: 1px solid var(--vo-line);
  flex-shrink: 0;
`;

const ColorHexText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${({ $placeholder }) => $placeholder ? BORDER_COLOR : INK};
`;

const HiddenColorInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  border: none;
  padding: 0;
`;

const PreviewWrapper = styled.div`
  background: var(--vo-mist);
  border-radius: 10px;
  padding: 20px;
`;

const PreviewLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${MUTED};
  margin: 0 0 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const BannerPreview = styled.div`
  background: ${({ $bg }) => $bg || 'var(--vo-escuro)'};
  border-radius: 10px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const BannerLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const BannerTitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $color }) => $color || 'var(--vo-white)'};
  margin: 0 0 4px;
`;

const BannerBody = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${({ $color }) => $color ? `${$color}B3` : 'color-mix(in srgb, var(--vo-white) 70%, transparent)'};
  margin: 0;
  line-height: 1.5;
`;

const PrivacyLink = styled.span`
  color: var(--vo-capri);
  text-decoration: underline;
  cursor: pointer;
`;

const BannerButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const RejectBtn = styled.button`
  height: 34px;
  background: transparent;
  color: ${({ $color }) => $color ? `${$color}B3` : 'color-mix(in srgb, var(--vo-white) 70%, transparent)'};
  border: 1px solid ${({ $color }) => $color ? `${$color}4D` : 'color-mix(in srgb, var(--vo-white) 30%, transparent)'};
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  cursor: pointer;
  padding: 0 14px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AcceptBtn = styled.button`
  height: 34px;
  background: ${({ $color }) => $color || 'var(--vo-cobalto)'};
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 16px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Native <input type="color">'s `value` attribute (HiddenColorInput below) requires a literal,
// browser-resolved hex string; a CSS custom property (var(--vo-...)) does not resolve there,
// confirmed via direct testing.
// eslint-disable-next-line no-color-literal
const ColorField = ({ label, value, onChange, placeholder = '#000000' }) => (
  <FieldGroup>
    <FieldLabel>{label}</FieldLabel>
    <ColorPickerRow>
      <ColorSwatch $color={value || placeholder} />
      <ColorHexText $placeholder={!value}>{value || placeholder}</ColorHexText>
      <HiddenColorInput
        type="color"
        value={value || placeholder}
        onChange={e => onChange(e.target.value)}
      />
    </ColorPickerRow>
  </FieldGroup>
);

export const Configuration = ({ id, register, control, handleSubmit, errors, getValues, handleComplete }) => {
  const { t } = useTranslation(['cookies']);

  const onSubmit = () => handleComplete();

  const { primaryColor, backgroundColor, textColor } = useWatch({ control });

  return (
    <form id="cookie-form" onSubmit={handleSubmit(onSubmit)}>
      <FormCard>
        <CardHeader>
          <CardIconBadge>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <circle cx="15" cy="9" r="1"/>
              <circle cx="12" cy="15" r="2"/>
            </svg>
          </CardIconBadge>
          <div>
            <CardTitle>{t('cookies:form.config.sectionTitle')}</CardTitle>
            <CardSubtitle>{t('cookies:form.config.sectionSubtitle')}</CardSubtitle>
          </div>
        </CardHeader>

        <FieldsStack>
          {/* URL field */}
          <FieldGroup data-tour="cookie-url">
            <FieldLabel>{t('cookies:form.config.urlLabel')} *</FieldLabel>
            <TextField
              fullWidth
              {...register('url', { required: t('cookies:form.config.urlRequired') })}
              type="url"
              placeholder="https://example.com"
              error={!!errors?.url}
              disabled={!!id}
              autoFocus={!id}
            />
            {errors?.url && <FieldError>{errors.url.message}</FieldError>}
            {!!id && <FieldHelper>{t('cookies:form.config.urlHelper')}</FieldHelper>}
          </FieldGroup>

          {/* Position select */}
          <FieldGroup data-tour="cookie-position">
            <FieldLabel>{t('cookies:form.config.positionLabel')} *</FieldLabel>
            <Controller
              control={control}
              name="positionController"
              rules={{
                validate: () => getValues('positionController') !== '',
                required: t('cookies:form.config.positionRequired'),
              }}
              render={({ field: { onChange, value } }) => (
                <Select
                  name="positionController"
                  value={value ?? ''}
                  onChange={onChange}
                  options={[
                    { value: '', label: t('cookies:form.config.positionPlaceholder') },
                    { value: 'left', label: t('cookies:form.config.positionLeft') },
                    { value: 'right', label: t('cookies:form.config.positionRight') },
                  ]}
                />
              )}
            />
            {errors?.positionController && <FieldError>{errors.positionController.message}</FieldError>}
          </FieldGroup>

          {/* Colors section */}
          <div data-tour="cookie-colors">
            <ColorsSectionTitle>{t('cookies:form.config.colorsTitle')}</ColorsSectionTitle>
            <ColorsGrid>
              <Controller
                control={control}
                name="primaryColor"
                rules={{ validate: () => getValues('primaryColor') !== '', required: t('cookies:form.config.primaryColorRequired') }}
                render={({ field: { onChange, value } }) => (
                  <ColorField label={`${t('cookies:form.config.primaryColorLabel')} *`} value={value} onChange={onChange} placeholder="var(--vo-cobalto)" />
                )}
              />
              <Controller
                control={control}
                name="backgroundColor"
                rules={{ validate: () => getValues('backgroundColor') !== '', required: t('cookies:form.config.bgColorRequired') }}
                render={({ field: { onChange, value } }) => (
                  <ColorField label={`${t('cookies:form.config.bgColorLabel')} *`} value={value} onChange={onChange} placeholder="var(--vo-escuro)" />
                )}
              />
              <Controller
                control={control}
                name="textColor"
                rules={{ validate: () => getValues('textColor') !== '', required: t('cookies:form.config.textColorRequired') }}
                render={({ field: { onChange, value } }) => (
                  <ColorField label={`${t('cookies:form.config.textColorLabel')} *`} value={value} onChange={onChange} placeholder="var(--vo-white)" />
                )}
              />
            </ColorsGrid>
          </div>

          {/* Banner preview */}
          <PreviewWrapper>
            <PreviewLabel>{t('cookies:form.config.previewLabel')}</PreviewLabel>
            <BannerPreview $bg={backgroundColor || 'var(--vo-escuro)'}>
              <BannerLeft>
                <BannerTitle $color={textColor || 'var(--vo-white)'}>
                  {t('cookies:form.config.previewTitle')}
                </BannerTitle>
                <BannerBody $color={textColor || null}>
                  {t('cookies:form.config.previewBody')}{' '}
                  <PrivacyLink>{t('cookies:form.config.previewPrivacyLink')}</PrivacyLink>.
                </BannerBody>
              </BannerLeft>
              <BannerButtons>
                <RejectBtn type="button" $color={textColor || null}>
                  {t('cookies:form.config.previewReject')}
                </RejectBtn>
                <AcceptBtn type="button" $color={primaryColor || 'var(--vo-cobalto)'}>
                  {t('cookies:form.config.previewAccept')}
                </AcceptBtn>
              </BannerButtons>
            </BannerPreview>
          </PreviewWrapper>
        </FieldsStack>
      </FormCard>
    </form>
  );
};
